import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    if (token) {
      fetchCart();
    }
  }, [token]);

  const fetchCart = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    if (!token) return;
    
    try {
      await axios.post(
        `${API}/cart/add`,
        { product_id: productId, quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCart();
    } catch (error) {
      console.error('Failed to add to cart:', error);
      throw error;
    }
  };

  const addHamperToCart = async (hamperData) => {
    if (!token) return;
    try {
      await axios.post(
        `${API}/cart/add-hamper`,
        hamperData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCart();
    } catch (error) {
      console.error('Failed to add hamper to cart:', error);
      throw error;
    }
  };

  const updateHamperInCart = async (hamperCartId, hamperData) => {
    if (!token) return;
    try {
      await axios.put(
        `${API}/cart/update-hamper/${hamperCartId}`,
        hamperData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCart();
    } catch (error) {
      console.error('Failed to update hamper in cart:', error);
      throw error;
    }
  };

  const removeHamperFromCart = async (hamperCartId) => {
    if (!token) return;
    try {
      await axios.post(
        `${API}/cart/remove-hamper?hamper_cart_id=${hamperCartId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCart();
    } catch (error) {
      console.error('Failed to remove hamper from cart:', error);
    }
  };

  const updateHamperQuantity = async (hamperCartId, quantity) => {
    if (!token) return;
    try {
      await axios.patch(
        `${API}/cart/hamper/${hamperCartId}/quantity`,
        { quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCart();
    } catch (error) {
      console.error('Failed to update hamper quantity:', error);
      throw error;
    }
  };

  const updateCart = async (productId, quantity) => {
    if (!token) return;
    
    try {
      await axios.post(
        `${API}/cart/update`,
        { product_id: productId, quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCart();
    } catch (error) {
      console.error('Failed to update cart:', error);
    }
  };

  const clearCart = async () => {
    if (!token) return;
    
    try {
      await axios.post(
        `${API}/cart/clear`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart({ items: [], total: 0 });
    } catch (error) {
      console.error('Failed to clear cart:', error);
    }
  };

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, addHamperToCart, updateHamperInCart, removeHamperFromCart, updateHamperQuantity, updateCart, clearCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
